import { createRef, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { useMedplum, useMedplumProfile } from "@medplum/react";

import {
  Button,
  Drawer,
  Flex,
  Group,
  Menu,
  Select,
  Stack,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { useForm, UseFormReturnType, zodResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";

import * as Sentry from "@sentry/react";
import { IconChevronDown, IconListCheck, IconListDetails, IconReplace } from "@tabler/icons-react";
import { v4 as uuidv4 } from "uuid";

import { TextWrapper } from "@/components/TextWrapper";
import { useAppDispatch } from "@/store";
import {
  createTemplate,
  getSelectedTemplate,
  setSelectedTemplate,
  upsertTemplate,
} from "@/store/slices/checklist-templates";
import { toast } from "@/utils/notifications";

import {
  CHECKLIST_TEMPLATE_SHARE_SETTINGS_OPTIONS,
  TemplateSubmitActions,
} from "../constants/checklist-template";
import { createChecklistTemplate } from "../services/template";
import { getChecklistTemplateInitialValues } from "../utils/template";
import {
  CHECKLIST_TEMPLATE_INITIAL_VALUES,
  ChecklistTemplateFormSchema,
  ChecklistTemplateFormType,
} from "../zod-schema/checklist-template-zod";

import ChecklistTaskDragAndDrop from "./ChecklistTaskDragAndDrop";

type ChecklistTemplateDrawerProps = {
  opened: boolean;
  onClose: () => void;
};

function ChecklistTemplateDrawer({ opened, onClose }: ChecklistTemplateDrawerProps) {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const dispatch = useAppDispatch();
  const [refs, setRefs] = useState<Record<string, React.RefObject<HTMLInputElement>>>({});
  const { practiceId } = useParams<{ practiceId: string }>();
  const selectedTemplate = useSelector(getSelectedTemplate);
  const [isSubmitting, { open: startSubmitting, close: stopSubmitting }] = useDisclosure(false);

  const form = useForm<ChecklistTemplateFormType>({
    initialValues: getChecklistTemplateInitialValues(
      CHECKLIST_TEMPLATE_INITIAL_VALUES,
      selectedTemplate,
    ),
    validate: zodResolver(ChecklistTemplateFormSchema),
  });

  useEffect(() => {
    form.setValues(
      getChecklistTemplateInitialValues(CHECKLIST_TEMPLATE_INITIAL_VALUES, selectedTemplate),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const onAdd = () => {
    const taskId = uuidv4();
    form.setFieldValue("tasks", { ...form.values.tasks, [taskId]: { id: taskId, name: "" } });
    const ref = createRef<HTMLInputElement>();
    setRefs((prev) => ({ ...prev, [taskId]: ref }));
  };

  const handleSubmit = async (
    values: ChecklistTemplateFormType,
    submitAction: TemplateSubmitActions = TemplateSubmitActions.Create,
  ) => {
    const validation = form.validate();
    if (validation.hasErrors) return;

    const isUpdate = submitAction === TemplateSubmitActions.Update;

    try {
      startSubmitting();

      if (!practiceId || !profile?.id) {
        toast({
          type: "error",
          title: "Unable to save template",
          message: "Practice or profile information is missing. Refresh the page and try again.",
          autoClose: false,
        });
        return;
      }

      const parsedValues = ChecklistTemplateFormSchema.parse(values);

      const template = await createChecklistTemplate({
        medplum,
        values: parsedValues,
        practiceId,
        practitionerId: profile.id,
        templateId: isUpdate ? selectedTemplate?.id : undefined,
      });

      if (isUpdate) {
        dispatch(upsertTemplate(template));
        toast({
          type: "success",
          title: "Template updated",
          message: "Your checklist template changes were saved successfully.",
          autoClose: 3000,
        });
      } else {
        dispatch(createTemplate(template));
        toast({
          type: "success",
          title: "Template created",
          message: "Your checklist template was created successfully.",
          autoClose: 3000,
        });
      }

      dispatch(setSelectedTemplate(null));
      onClose();
      form.reset();
    } catch (error) {
      Sentry.captureException(error);
      toast({
        type: "error",
        title: isUpdate ? "Failed to update template" : "Failed to create template",
        message: "Something went wrong while saving the checklist template. Please try again.",
        autoClose: false,
      });
    } finally {
      stopSubmitting();
    }
  };

  const handleClose = () => {
    onClose();
    dispatch(setSelectedTemplate(null));
    form.reset();
  };

  return (
    <Drawer.Root
      size={512}
      opened={opened}
      position="right"
      onClose={handleClose}
      closeOnEscape={false}
      closeOnClickOutside={false}
    >
      <Drawer.Overlay />
      <Drawer.Content>
        <form
          onSubmit={form.onSubmit((values) => handleSubmit(values, TemplateSubmitActions.Create))}
        >
          <Drawer.Header h={64} px="md" bg="gray.0">
            <Flex w="100%" gap="md" align="center" justify="space-between">
              <Flex gap="md" align="center" style={{ flex: 1 }}>
                <Drawer.CloseButton data-testid="close-template-drawer-button" />
                <Drawer.Title style={{ flex: 1 }}>
                  <TextWrapper fz="lg" variant="bold" style={{ wordBreak: "break-word" }}>
                    Add Checklist Template
                  </TextWrapper>
                </Drawer.Title>
              </Flex>

              {selectedTemplate ? (
                <SaveOptionsMenu
                  isSubmitting={isSubmitting}
                  onSubmit={(action) => handleSubmit(form.values, action)}
                />
              ) : (
                <Button type="submit" loading={isSubmitting}>
                  Save
                </Button>
              )}
            </Flex>
          </Drawer.Header>
          <Drawer.Body p="lg">
            <Stack gap="lg">
              <TextInput required label="Template Name" {...form.getInputProps("name")} />
              <Select
                label="Share Settings"
                data={CHECKLIST_TEMPLATE_SHARE_SETTINGS_OPTIONS}
                description="Public checklist will be shared with the rest of the practice"
                {...form.getInputProps("shareSettings")}
              />
              <ChecklistTemplateTasks refs={refs} form={form} onAdd={onAdd} />
            </Stack>
          </Drawer.Body>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
}

const ChecklistTemplateTasks = ({
  refs,
  form,
  onAdd,
}: {
  refs: Record<string, React.RefObject<HTMLInputElement>>;
  form: UseFormReturnType<ChecklistTemplateFormType>;
  onAdd: () => void;
}) => {
  const tasks = Object.values(form.values.tasks);

  return (
    <Stack>
      <TextWrapper fw={600} size="lg" c="gray.6">
        Tasks
      </TextWrapper>
      {!tasks.length && <ChecklistTemplateTasksEmpty onAdd={onAdd} />}
      {form.errors.tasks && (
        <TextWrapper size="sm" c="red.5">
          {form.errors.tasks}
        </TextWrapper>
      )}
      <ChecklistTaskDragAndDrop form={form} refs={refs} tasks={tasks} />
      {!!tasks.length && (
        <UnstyledButton
          fw={600}
          c="burgundy.9"
          td="underline"
          onClick={onAdd}
          style={{ width: "fit-content" }}
        >
          + Add a task
        </UnstyledButton>
      )}
    </Stack>
  );
};

const ChecklistTemplateTasksEmpty = ({ onAdd }: { onAdd: () => void }) => {
  return (
    <Flex py="lg" mx={-8} gap="xs" bg="gray.0" align="center" justify="center">
      <IconListCheck style={{ color: "var(--mantine-color-gray-6)" }} />
      <Group gap={4}>
        <TextWrapper c="gray.7">No tasks have been added.</TextWrapper>
        <UnstyledButton onClick={onAdd}>
          <TextWrapper c="gray.7" variant="bold" td="underline">
            Add a task now
          </TextWrapper>
        </UnstyledButton>
      </Group>
    </Flex>
  );
};

export const SaveOptionsMenu = ({
  onSubmit,
  isSubmitting,
}: {
  onSubmit: (action: TemplateSubmitActions) => void;
  isSubmitting: boolean;
}) => {
  return (
    <Menu>
      <Menu.Target>
        <Button loading={isSubmitting} leftSection={<IconChevronDown size={14} />}>
          Save
        </Button>
      </Menu.Target>

      <Menu.Dropdown style={{ gap: 20 }}>
        <Menu.Item
          leftSection={<IconListDetails size={14} />}
          onClick={() => onSubmit(TemplateSubmitActions.Create)}
        >
          As new template
        </Menu.Item>
        <Menu.Item
          leftSection={<IconReplace size={14} />}
          onClick={() => onSubmit(TemplateSubmitActions.Update)}
        >
          Update
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

export default ChecklistTemplateDrawer;
